// Footer.jsx
import { motion } from "framer-motion";
import Box from '@mui/material/Box';
import { useNavigate } from "react-router-dom";

import FacebookOutlinedIcon from '@mui/icons-material/FacebookOutlined';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { gerarLinkWhatsApp } from "../../utils/WhatsappLink";

const linkSx = {
    color: "rgba(255, 255, 255, 0.72)",
    fontSize: "0.92rem",
    cursor: "pointer",
    textDecoration: "none",
    width: "fit-content",
    transition: "color 0.2s ease",
    "&:hover": { color: "#ffc845" },
};

const tituloColunaSx = {
    mb: 2,
    fontSize: "0.8rem",
    fontWeight: 700,
    letterSpacing: "1px",
    textTransform: "uppercase",
    color: "#ffc845",
};

const Footer = () => {
    const navigate = useNavigate();

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const blockVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: i => ({
            opacity: 1,
            y: 0,
            transition: { delay: i * 0.15, duration: 0.6 }
        }),
    };

    const socialIcons = [
        { label: "Facebook", icon: FacebookOutlinedIcon, link: "https://www.facebook.com/people/Clarea-Vital/61582558775457" },
        { label: "Instagram", icon: InstagramIcon, link: "https://www.instagram.com/clareavital" },
        { label: "LinkedIn", icon: LinkedInIcon, link: "https://www.linkedin.com/company/clarea-vital" },
    ];

    const navegacao = [
        { label: "Página inicial", path: "/" },
        { label: "Sobre nós", path: "../SobreNos" },
        { label: "Planos", path: "../Planos" },
        { label: "Soluções", path: "../Solucoes" },
        { label: "Nichos", path: "../Nichos" },
    ];

    const servicos = [
        { label: "Abertura de empresa", path: "../Solucoes" },
        { label: "Entrega de declarações", path: "../Solucoes" },
        { label: "Troque de contador", path: "../Solucoes" },
    ];

    return (
        <Box
            component="footer"
            sx={{
                position: "relative",
                width: "100%",
                background: "linear-gradient(180deg, #0b243d 0%, #081b2e 100%)",
                color: "#fff",
                overflow: "hidden",
            }}
        >
            {/* Detalhe decorativo */}
            <Box
                sx={{
                    position: "absolute",
                    top: { xs: -100, md: -140 },
                    right: { xs: -100, md: -140 },
                    width: { xs: 240, md: 340 },
                    height: { xs: 240, md: 340 },
                    borderRadius: "50%",
                    border: { xs: "36px solid #ffc845", md: "52px solid #ffc845" },
                    opacity: 0.07,
                    pointerEvents: "none",
                }}
            />

            <Box sx={{ position: "relative", width: "90%", maxWidth: "1280px", mx: "auto", pt: { xs: 7, md: 9 }, pb: { xs: 5, md: 6 } }}>
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                >
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1.6fr 1fr 1fr 1.3fr" },
                            gap: { xs: 5, md: 6 },
                        }}
                    >
                        {/* Marca + redes */}
                        <motion.div custom={0} variants={blockVariants}>
                            <Box
                                component="img"
                                src="/assets/logo_clarea-02.png"
                                alt="Logo Clarea"
                                sx={{ width: "100%", maxWidth: "220px", display: "block", mb: 2 }}
                            />
                            <Box component="p" sx={{ m: "0 0 20px", maxWidth: "320px", fontSize: "0.92rem", lineHeight: 1.7, color: "rgba(255, 255, 255, 0.72)" }}>
                                A contabilidade para quem cuida da saúde. Simples, experiente e 100% online.
                            </Box>
                            <Box sx={{ display: "flex", gap: 1.25 }}>
                                {socialIcons.map((item) => {
                                    const Icon = item.icon;
                                    return (
                                        <Box
                                            key={item.label}
                                            component="a"
                                            href={item.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={item.label}
                                            sx={{
                                                width: 42,
                                                height: 42,
                                                borderRadius: "12px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "#fff",
                                                backgroundColor: "rgba(255, 255, 255, 0.06)",
                                                border: "1px solid rgba(255, 255, 255, 0.12)",
                                                transition: "all 0.2s ease",
                                                "&:hover": {
                                                    color: "#0b243d",
                                                    backgroundColor: "#ffc845",
                                                    borderColor: "#ffc845",
                                                    transform: "translateY(-2px)",
                                                },
                                            }}
                                        >
                                            <Icon sx={{ fontSize: 20 }} />
                                        </Box>
                                    );
                                })}
                            </Box>
                        </motion.div>

                        {/* Navegação */}
                        <motion.div custom={1} variants={blockVariants}>
                            <Box sx={tituloColunaSx}>Navegação</Box>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                {navegacao.map((item) => (
                                    <Box key={item.label} component="span" sx={linkSx} onClick={() => navigate(item.path)}>
                                        {item.label}
                                    </Box>
                                ))}
                            </Box>
                        </motion.div>

                        {/* Serviços */}
                        <motion.div custom={2} variants={blockVariants}>
                            <Box sx={tituloColunaSx}>Serviços</Box>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                {servicos.map((item) => (
                                    <Box key={item.label} component="span" sx={linkSx} onClick={() => navigate(item.path)}>
                                        {item.label}
                                    </Box>
                                ))}
                            </Box>
                        </motion.div>

                        {/* Contato */}
                        <motion.div custom={3} variants={blockVariants}>
                            <Box sx={tituloColunaSx}>Contato</Box>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
                                <Box component="a" href="tel:+5541984229975" sx={{ ...linkSx, display: "flex", alignItems: "center", gap: 1.25, color: "#fff", fontSize: "1.05rem", fontWeight: 600 }}>
                                    <PhoneOutlinedIcon sx={{ fontSize: 20, color: "#ffc845" }} />
                                    (41) 98422-9975
                                </Box>
                                <Box component="a" href="mailto:personal@clareavital.com.br" sx={{ ...linkSx, display: "flex", alignItems: "center", gap: 1.25, wordBreak: "break-all" }}>
                                    <EmailOutlinedIcon sx={{ fontSize: 20, color: "#ffc845" }} />
                                    personal@clareavital.com.br
                                </Box>
                                <Box
                                    component="a"
                                    href={gerarLinkWhatsApp("Olá! Vim pelo site e gostaria de atendimento.")}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        mt: 0.5,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 1,
                                        width: "fit-content",
                                        px: 2,
                                        py: 1.1,
                                        borderRadius: "10px",
                                        backgroundColor: "#25D366",
                                        color: "#fff",
                                        fontSize: "0.9rem",
                                        fontWeight: 700,
                                        textDecoration: "none",
                                        transition: "all 0.2s ease",
                                        "&:hover": { backgroundColor: "#1ebe5b", transform: "translateY(-2px)" },
                                    }}
                                >
                                    <WhatsAppIcon sx={{ fontSize: 20 }} />
                                    Fale no WhatsApp
                                </Box>
                            </Box>
                        </motion.div>
                    </Box>
                </motion.div>
            </Box>

            {/* Barra inferior */}
            <Box sx={{ position: "relative", borderTop: "1px solid rgba(255, 255, 255, 0.1)" }}>
                <Box
                    sx={{
                        width: "90%",
                        maxWidth: "1280px",
                        mx: "auto",
                        py: 2.5,
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1.5,
                        fontSize: "0.85rem",
                        color: "rgba(255, 255, 255, 0.6)",
                    }}
                >
                    <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
                        © {new Date().getFullYear()} Clarea. Todos os direitos reservados.
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                        <Box component="span" sx={{ ...linkSx, fontSize: "0.85rem", color: "rgba(255, 255, 255, 0.6)" }} onClick={() => navigate("/politica-de-privacidade")}>
                            Privacidade
                        </Box>
                        <Box component="span" sx={{ ...linkSx, fontSize: "0.85rem", color: "rgba(255, 255, 255, 0.6)" }} onClick={() => navigate("/termos-de-uso")}>
                            Termos de uso
                        </Box>
                        <Box
                            component="button"
                            type="button"
                            onClick={scrollToTop}
                            aria-label="Voltar ao topo da página"
                            sx={{
                                width: 38,
                                height: 38,
                                p: 0,
                                borderRadius: "10px",
                                border: "1px solid rgba(255, 200, 69, 0.5)",
                                backgroundColor: "transparent",
                                color: "#ffc845",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                "&:hover": { backgroundColor: "#ffc845", color: "#0b243d" },
                            }}
                        >
                            <KeyboardArrowUpIcon />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default Footer;
